import Module from '../classes/Module'
import { app } from '../utils/store'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const IN_VIEWPORT_CLASS = 'app-viewport--inview'
const COMPLETE_CLASS = 'app-viewport--complete'
const IN_VIEWPORT_AT = 0.25

export class Viewport extends Module {
    constructor() {
        super({
            element: '[data-app-viewport]',
            elements: { inner: '[data-wrapper-inner]' },
        })

        this.clipInner = null
        this.clipTween = null
        this.preserveInView = false
        this.preserveComplete = false
        this.onResize = this.debounce(() => this.handleResize(), 150)
    }

    create() {
        super.create()

        if (this.elements.inner && app.viewport) {
            this.initClipScroll()
        }
    }

    initClipScroll() {
        this.destroyClipScroll()

        const inner = this.elements.inner
        const scroller = app.viewport
        const viewport = this

        if (!inner || !scroller) { return}

        const maxInset = Viewport.measureMaxInset(inner)

        if (!maxInset) { return }

        this.clipInner = inner
        inner.style.transition = 'none'

        const clipState = { inset: maxInset }

        Viewport.applyClipInset(inner, clipState.inset)

        this.clipTween = gsap.fromTo(
            clipState,
            {
                inset: () => Viewport.measureMaxInset(inner) || 0,
            },
            {
                inset: 0,
                ease: 'none',
                onUpdate() {
                    const state = this.targets()[0]

                    if (!state) { return }
                    Viewport.applyClipInset(inner, state.inset)
                    Viewport.toggleInViewport(inner, this.progress() >= IN_VIEWPORT_AT)

                    if (this.progress() >= 1) {
                        app.body.classList.add(COMPLETE_CLASS)
                    } else {
                        app.body.classList.remove(COMPLETE_CLASS)
                    }
                },

                scrollTrigger: {
                    trigger: inner,
                    scroller,
                    start: 'top 90%',
                    end: 'top center+=100px',
                    scrub: 1.25,
                    onRefresh() {
                        if (viewport.preserveInView) {
                            app.body.classList.add(IN_VIEWPORT_CLASS)
                        }

                        if (viewport.preserveComplete) {
                            app.body.classList.add(COMPLETE_CLASS)
                            Viewport.applyClipInset(inner, 0)
                        }
                    },
                },
            },
        )
    }

    static applyClipInset(element, px) {
        element?.style.setProperty('--clip-inset', `${px}px`)
    }

    static toggleInViewport(element, active) {
        app.body.classList.toggle(IN_VIEWPORT_CLASS, active)
    }

    /**
     * Rilegge il clamp() dal CSS (rimuove l'inline impostato da GSAP).
     */
    static measureMaxInset(element) {
        element.style.removeProperty('--clip-inset')

        return Viewport.getResolvedClipInset(element) || 0
    }

    /**
     * Risolve clamp()/calc() in pixel misurando la variabile CSS nel layout reale.
     */
    static getResolvedClipInset(element) {
        const { clipPath } = getComputedStyle(element)

        if (!clipPath || clipPath === 'none') {
            return null
        }

        const probe = document.createElement('div')

        probe.setAttribute('aria-hidden', 'true')
        probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;width:var(--clip-inset);height:0;overflow:hidden;'
        element.appendChild(probe)

        const px = probe.getBoundingClientRect().width
        element.removeChild(probe)

        return px > 0 ? px : null
    }

    captureViewportState() {
        this.preserveInView = app.body.classList.contains(IN_VIEWPORT_CLASS)
        this.preserveComplete = app.body.classList.contains(COMPLETE_CLASS)
    }

    handleResize() {
        const inner = this.clipInner

        if (!inner || !this.clipTween) { return }

        requestAnimationFrame(() => {
            if (this.preserveInView) {
                app.body.classList.add(IN_VIEWPORT_CLASS)
            }

            if (this.preserveComplete) {
                app.body.classList.add(COMPLETE_CLASS)
                Viewport.applyClipInset(inner, 0)
            }

            this.preserveInView = false
            this.preserveComplete = false
        })
    }

    debounce(execution, wait) {
        let timeout

        return (...args) => {
            clearTimeout(timeout)
            timeout = setTimeout(() => execution(...args), wait)
        }
    }

    destroyClipScroll({ resetClasses = true } = {}) {
        const tween = this.clipTween
        const inner = this.clipInner

        this.clipTween = null
        this.clipInner = null
        this.preserveInView = false
        this.preserveComplete = false

        tween?.scrollTrigger?.kill()
        tween?.kill()

        inner?.style.removeProperty('--clip-inset')

        if (resetClasses) {
            Viewport.toggleInViewport(inner, false)
            app.body.classList.remove(COMPLETE_CLASS)
        }
    }

    addEventListeners() {
        this.resizeHandler = () => {
            this.captureViewportState()
            this.onResize()
        }

        window.addEventListener('resize', this.resizeHandler, true)
    }

    removeEventListeners() {
        window.removeEventListener('resize', this.resizeHandler, true)
        this.destroyClipScroll()
    }
}


